import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { ASSIGNABLE_ROLES } from '../../employeesApi';
import type { Employee, EmployeeRole } from '../../employeesApi';
import { useChangeEmployeeRole } from '../../hooks';

interface ChangeRoleDialogProps {
  /** The employee whose role is being changed; `null` closes the dialog. */
  employee: Employee | null;
  onClose: () => void;
}

/**
 * Change an employee's portal role.
 *
 * <p>The endpoint, the API function and `useChangeEmployeeRole` all existed and were tested before this
 * dialog did — nothing called the hook, so changing someone's role meant removing and re-inviting them
 * (GAP F-9a). This is the missing call site.
 *
 * <p>The BFF refuses two cases and this dialog surfaces both rather than letting them arrive as a bare
 * 400: an administrator changing <em>their own</em> role (the staff list already disables the whole row
 * menu for the caller's row, so it cannot normally be reached from here), and demoting the last remaining
 * OWNER (the row menu disables the entry, and the message still lands here if the count changed under a
 * paged view). Like every other action on this screen, the dialog closes only on success — a refusal keeps
 * it open and says why.
 */
export function ChangeRoleDialog({ employee, onClose }: ChangeRoleDialogProps) {
  const { t } = useTranslation('employees');
  const change = useChangeEmployeeRole();
  const [role, setRole] = useState<EmployeeRole>('STAFF');

  // The dialog stays mounted between openings, so the selector is re-seeded from whichever employee is
  // being edited now — otherwise the second person opened would inherit the first one's selection.
  // A role-less member (`role: null`) has no current value to start from, so STAFF is the floor; an
  // OWNER cannot be re-selected as OWNER, since OWNER is not an assignable role here.
  useEffect(() => {
    if (employee) {
      setRole(employee.role && employee.role !== 'OWNER' ? employee.role : 'STAFF');
    }
  }, [employee]);

  // Every exit runs through here — Cancel, the backdrop, and success — so a refusal shown in the dialog
  // is always cleared before it can reappear over a different employee.
  const close = () => {
    change.reset();
    onClose();
  };

  const unchanged = employee?.role === role;

  return (
    <Dialog open={employee !== null} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{t('changeRoleDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <DialogContentText>
            {t('changeRoleDialog.body', { email: employee?.email ?? '' })}
          </DialogContentText>
          {change.isError && <Alert severity="error">{change.error?.message}</Alert>}
          <TextField
            select
            label={t('changeRoleDialog.roleLabel')}
            value={role}
            onChange={(e) => setRole(e.target.value as EmployeeRole)}
            helperText={t('changeRoleDialog.roleHelper')}
            fullWidth
          >
            {ASSIGNABLE_ROLES.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`roles.${option.toLowerCase()}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={change.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!employee || unchanged || change.isPending}
          startIcon={change.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() =>
            employee && change.mutate({ id: employee.id, role }, { onSuccess: close })
          }
        >
          {t('changeRoleDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
